import React, { Component } from 'react'
import { Route, Link } from 'react-router-dom'
import { RouteComponentProps } from 'react-router'
import { Layout, Menu, Image } from 'antd'
import { LogoutOutlined } from '@ant-design/icons'
import { BaseComponent } from '../BaseComponent'
import { ThemeContext, ThemeContextData } from '../../context/ThemeContext'
import { getActiveMenu } from '../../helpers/navigation.helper'
import IAdminUser from '../../interfaces/IAdminUser'
import NavRoutes, { MenuItems, MenuGroupType, MenuItemType } from '../../navigation'
import './style.less'

type ApplicationProps = RouteComponentProps & {
  user: IAdminUser,
  signOut: Function,
  getSessionInfo: Function
}

const { Header, Content, Sider } = Layout

const SUPPORT_WIDGET_CLASS = 'support-widget-enabled'

export default class Application extends Component<ApplicationProps> {

  static contextType = ThemeContext

  state = {
    historyStack: [] as string[]
  }

  public get _context() {
    return this.context as ThemeContextData
  }

  componentDidMount() {
    const { getSessionInfo, history } = this.props

    document.body.classList.add(SUPPORT_WIDGET_CLASS)

    getSessionInfo()

    history.listen((location, action) => {
      let newState = Object.assign({}, this.state)

      if (action === 'PUSH') {
        newState.historyStack.push(location.pathname)
      }
      if (action === 'POP') {
        const index = newState.historyStack.lastIndexOf(location.pathname)
        
        if (index >= 0) {
          newState.historyStack = newState.historyStack.slice(0, index)
        }
      }
      if (action === 'REPLACE') {
        const index = newState.historyStack.lastIndexOf(location.pathname)

        if (index >= 0) {
          newState.historyStack[index] = location.pathname
        } else {
          newState.historyStack.push(location.pathname)
        }
      }

      this.setState(newState)
    })
  }

  componentWillUnmount() {
    document.body.classList.remove(SUPPORT_WIDGET_CLASS)
  }

  checkUserPermission = (userPermissions: string[], path: string) => {
    const navRoute = NavRoutes.find(route => {
      const idx = route.path.indexOf(':') > 0 ? route.path.indexOf(':') - 1 : route.path.length
      const routePath = route.path.slice(0, idx)

      return routePath === path
    })

    // Does user has permissions for listing?
    if (navRoute?.permissions?.list) {
      if (typeof navRoute.permissions.list === 'string' && !userPermissions.includes(navRoute.permissions.list)) {
        return false
      }
    }

    return true
  }

  /**
   * Smart history pop
   */
  goBack = (fallbackRoute = '/') => {
    const { history } = this.props
    const { historyStack } = this.state

    // There's no previous routes, so we should go to home :D
    if (historyStack.length === 0) {
      history.replace(fallbackRoute)
    } else {
      history.goBack()
    }
  }

  /**
   * Handle the side menu collapse
   * We need to know when the side menu is collapsed,
   * because we resize the length of the logo, then we do not break the layout
   */
  handleSideMenuCollapse = (collapsed: boolean) => {
    this._context.setSideMenuCollapse(collapsed)
  }

  renderSideMenu = (userPermissions: string[]) => {
    const { sideMenuCollapsed } = this._context
    const { defaultSelectedKeys, defaultOpenKeys } = getActiveMenu(this.props)

    return (
      <Sider collapsible collapsed={sideMenuCollapsed} width={250} style={{height: '100vh', position: 'fixed'}} onCollapse={this.handleSideMenuCollapse}>
        <div className="logo">
          <Image
            src={
              !sideMenuCollapsed ? require('../../assets/images/adopets-primary-logo-v3.png') : require('../../assets/images/adopets-tiny-logo.ico')
            }
          />
        </div>

        <Menu theme="dark" mode="inline" defaultSelectedKeys={defaultSelectedKeys} defaultOpenKeys={sideMenuCollapsed ? [] : defaultOpenKeys}>
          {
            MenuItems.map((menu: any, index) => {
              if (menu.items) {
                const { name, icon, items } = menu as MenuGroupType
                
                const validRoutes = items.filter(item => {
                  return this.checkUserPermission(userPermissions, item.path)
                })

                // Submenu has no valid routes
                if (validRoutes.length === 0) {
                  return null
                }

                return (
                  <Menu.SubMenu key={`menu-item-${index}`} title={name} icon={icon}>
                    {
                      items.filter(v => !v.hide).map((subMenu: MenuItemType, subIndex) => {
                        const { path, name, icon } = subMenu

                        // Has permission?
                        if (!this.checkUserPermission(userPermissions, path)) {
                          return null
                        }

                        return (
                          <Menu.Item key={`menu-item-${index}-sub-${subIndex}`} icon={icon}>
                            <Link to={path}>
                              {name}
                            </Link>
                          </Menu.Item>
                        )
                      })
                    }
                  </Menu.SubMenu>
                )
              } else {
                // Not a submenu
                const { path, name, icon } = menu as MenuItemType
                
                // Has permission?
                if (!this.checkUserPermission(userPermissions, path)) {
                  return null
                }

                return (
                  <Menu.Item key={`menu-item-${index}`} icon={icon}>
                    <Link to={path}>
                      {name}
                    </Link>
                  </Menu.Item>
                )
              }
            })
          }
        </Menu>
      </Sider>
    )
  }

  render() {
    const { user } = this.props
    const { sideMenuCollapsed } = this._context

    console.log("user",user)

    const userPermissions: string[] = []
    user.roles?.map(role => role.permissions.map(permission => permission.child)).forEach(role => {
      role.forEach((perm: string) => {
        if (!userPermissions.includes(perm)) {
          userPermissions.push(perm)
        }
      })
    })

    return (
      <Layout style={{height: '100%'}}>
        <div style={{width: sideMenuCollapsed ? 80 : 250, minWidth: sideMenuCollapsed ? 80 : 250, height: '100vh'}}>
          {
            // Render responsive side menu
            this.renderSideMenu(userPermissions)
          }
        </div>

        <Layout id="mainLayout">
          <Header className="site-layout-sub-header-background">
            <Menu theme="dark" mode="horizontal">
              <Menu.Item key="2" style={{float: 'right'}} onClick={() => this.props.signOut()}>
                {
                  !sideMenuCollapsed ? 'Logout' : <LogoutOutlined />
                }
              </Menu.Item>
            </Menu>
          </Header>

          <Content style={{ margin: '24px 16px 0' }}>
            <div style={{ padding: 24, minHeight: '80vh' }}>
              {
                NavRoutes.filter(p => p.isPrivate).map((route, index) => {
                  const { path, name, exact, permissions, component: Component } = route

                  return (
                    <Route
                      key={`app-route-${index}`}
                      path={path}
                      exact={exact}
                      render={(props) => (
                        <BaseComponent {...props} name={name} permissions={permissions} userPermissions={userPermissions}>
                          {({ basePermissions }) => <Component {...props} permissions={basePermissions} goBack={this.goBack} />}
                        </BaseComponent>
                      )}
                    />
                  )
                })
              }
            </div>
          </Content>
        </Layout>
      </Layout>
    )
  }
}
